import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  ReleaseNote,
  ReleaseNoteHistoryItem
} from '@core/models/release-note.model';

/**
 * Service de notes de version (changelog).
 *
 * Source de vérité = backend (état "lu" persisté en BDD côté
 * `UserReleaseNoteView`), ce qui garantit un fonctionnement multi-device cohérent.
 *
 * Le `localStorage` n'est utilisé qu'à titre de cache opportuniste pour éviter
 * un flash de modale au reload entre deux refresh de l'API.
 */
@Injectable({ providedIn: 'root' })
export class ReleaseNotesService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}/api/release-notes`;

  /** Clé localStorage du dernier id "vu" (cache opportuniste, non source de vérité). */
  private static readonly LAST_SEEN_KEY = 'release_notes_last_seen_id';

  private readonly _unread$ = new BehaviorSubject<ReleaseNote[]>([]);
  readonly unread$ = this._unread$.asObservable();
  readonly unreadCount$ = this.unread$.pipe(map(list => list.length));

  /** Snapshot synchrone de la liste des notes non lues. */
  get unread(): ReleaseNote[] {
    return this._unread$.value;
  }

  /** Recharge la liste des notes non lues depuis l'API. Ne lève pas en cas d'erreur réseau. */
  refreshUnread(): Observable<ReleaseNote[]> {
    return this.http.get<ReleaseNote[]>(`${this.base}/unread`).pipe(
      tap(list => this._unread$.next(list ?? [])),
      catchError(err => {
        console.warn('[ReleaseNotesService] Échec du refresh des notes non lues.', err);
        return of([] as ReleaseNote[]);
      })
    );
  }

  /** Marque une note comme lue (idempotent côté serveur). */
  markAsRead(id: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/mark-as-read`, {}).pipe(
      tap(() => {
        const remaining = this._unread$.value.filter(n => n.id !== id);
        this._unread$.next(remaining);
        this.rememberLastSeen(id);
      })
    );
  }

  /** Marque toutes les notes non lues comme lues. */
  markAllAsRead(): Observable<void> {
    return this.http.post<void>(`${this.base}/mark-all-as-read`, {}).pipe(
      tap(() => {
        const lastId = Math.max(0, ...this._unread$.value.map(n => n.id));
        this._unread$.next([]);
        if (lastId > 0) this.rememberLastSeen(lastId);
      })
    );
  }

  /** Historique paginé (lues + non lues). */
  history(skip = 0, take = 20): Observable<ReleaseNoteHistoryItem[]> {
    const params = `?skip=${skip}&take=${take}`;
    return this.http.get<ReleaseNoteHistoryItem[]>(`${this.base}/history${params}`);
  }

  /** Réinitialise le cache local (à appeler au logout). */
  clear(): void {
    this._unread$.next([]);
    try {
      localStorage.removeItem(ReleaseNotesService.LAST_SEEN_KEY);
    } catch { /* ignore quota / private mode */ }
  }

  private rememberLastSeen(id: number): void {
    try {
      const previous = Number(localStorage.getItem(ReleaseNotesService.LAST_SEEN_KEY) ?? 0);
      if (id > previous) {
        localStorage.setItem(ReleaseNotesService.LAST_SEEN_KEY, String(id));
      }
    } catch { /* ignore */ }
  }
}
